import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Colors, Spacing, BorderRadius } from '../constants/theme';

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const SpinnerContainer = styled.div<{ size?: 'sm' | 'md' | 'lg'; inline?: boolean }>`
  display: ${props => props.inline ? 'inline-flex' : 'flex'};
  align-items: center;
  justify-content: center;
  padding: ${props => props.inline ? '0' : `${Spacing.lg}px`};
`;

const Spinner = styled.div<{ size?: 'sm' | 'md' | 'lg' }>`
  width: ${props => {
    switch (props.size) {
      case 'sm': return '16px';
      case 'lg': return '48px';
      default: return '32px';
    }
  }};
  height: ${props => {
    switch (props.size) {
      case 'sm': return '16px';
      case 'lg': return '48px';
      default: return '32px';
    }
  }};
  border: 3px solid ${Colors.border};
  border-top: 3px solid ${Colors.primary};
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

const LoadingText = styled.span<{ size?: 'sm' | 'md' | 'lg' }>`
  margin-left: ${Spacing.sm}px;
  color: ${Colors.textSecondary};
  font-size: ${props => {
    switch (props.size) {
      case 'sm': return '12px';
      case 'lg': return '18px';
      default: return '14px';
    }
  }};
`;

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  text?: string;
  inline?: boolean;
  ariaLabel?: string;
}

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ 
  size = 'md', 
  text, 
  inline = false,
  ariaLabel = 'Loading content'
}) => {
  const { t } = useTranslation();
  
  const defaultAriaLabel = t('common.loadingContent', 'Loading content');
  const finalAriaLabel = ariaLabel === 'Loading content' ? defaultAriaLabel : ariaLabel;
  const displayText = text || t('common.loading', 'Loading...');
  
  return (
    <SpinnerContainer 
      size={size} 
      inline={inline}
      role="status"
      aria-label={finalAriaLabel}
      aria-live="polite"
    >
      <Spinner size={size} aria-hidden="true" />
      {text && <LoadingText size={size}>{displayText}</LoadingText>}
      <span className="sr-only">{displayText}</span>
    </SpinnerContainer>
  );
};

export default LoadingSpinner;